import { Stack, LoadingButton } from "@altimateai/lego";
import { KeyboardEvent, useEffect } from "react";
import { ApiHelper, useMutation } from "../../api/index";
import {
  ProjectGovernorCheckFormValues,
  ProjectGovernorCheckTypes,
  ProjectGovernorCheckValidateResponse,
} from "./types";
import {
  Button,
  NativeSelect,
  Label,
  FormItem,
  FormMessage as FormFeedback,
  Textarea,
} from "@altimateai/lego";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// Define validation schema
const checkFormSchema = z.object({
  description: z.string().min(1, { message: "Description is required" }),
  type: z.nativeEnum(ProjectGovernorCheckTypes),
});

const ChecksInput = ({
  onSuccess,
  onCancel,
}: {
  onSuccess: (data: ProjectGovernorCheckValidateResponse, type: ProjectGovernorCheckTypes) => void;
  onCancel: () => void;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setError,
    watch,
    setValue,
  } = useForm<ProjectGovernorCheckFormValues>({
    resolver: zodResolver(checkFormSchema),
    defaultValues: {
      description: "",
      type: ProjectGovernorCheckTypes.MODEL,
    },
  });

  const values = watch();

  const onSubmit = async (data: ProjectGovernorCheckFormValues) => {
    return await ApiHelper.post<ProjectGovernorCheckValidateResponse>(
      `project_governance/check/validate`,
      { ...data }
    );
  };

  const { error, loading } = useMutation({
    queryFn: () => onSubmit(values),
    onSuccess: (data: ProjectGovernorCheckValidateResponse) => {
      onSuccess(data, values.type);
    },
  });

  useEffect(() => {
    if (error?.message) {
      setError("description", { message: error.message });
    }
  }, [error, setError]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const input = e.target as HTMLTextAreaElement;
    input.style.height = "inherit";
    input.style.height = `${Math.min(input.scrollHeight + 5, 200)}px`;
  };

  return (
    <form onSubmit={handleSubmit(data => onSubmit(data).then(res => onSuccess(res, data.type)))}>
      <FormItem className="al-mb-4">
        <Textarea
          {...register("description")}
          onKeyDown={handleKeyDown}
          placeholder="Describe the check in natural language"
          className={
            errors.description ? "al-border-destructive al-min-h-[100px]" : "al-min-h-[100px]"
          }
        />
        {errors.description ? <FormFeedback>{errors.description.message}</FormFeedback> : null}
      </FormItem>
      <FormItem>
        <Label>Type: </Label>
        <NativeSelect
          style={{ width: 200 }}
          placeholder="Select type"
          value={values.type}
          onChange={(value: string) => setValue("type", value as ProjectGovernorCheckTypes)}
          options={Object.entries(ProjectGovernorCheckTypes).map(([, value]) => ({
            value: value,
            label: value,
          }))}
        />
      </FormItem>
      <Stack className="al-justify-end">
        <Button variant="ghost" onClick={onCancel} type="button">
          Cancel
        </Button>
        <LoadingButton
          color="primary"
          loading={loading || isSubmitting}
          type="submit"
          disabled={!isValid || values?.description?.length === 0}
        >
          Submit
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default ChecksInput;
